import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { extractYoutubeId, youtubeEmbedUrl } from '../lib/youtube';
import type { YoutubeBlockData } from '../types';

interface YoutubeBlockEditorProps {
  data: YoutubeBlockData;
  onChange: (data: YoutubeBlockData) => void;
}

/** URL/id input → privacy-enhanced (youtube-nocookie) embed preview. Only the
 *  extracted 11-char id is stored; the server re-validates it. */
export function YoutubeBlockEditor({ data, onChange }: YoutubeBlockEditorProps) {
  const { t } = useTranslation(['curriculum']);
  const inputId = useId();
  const [raw, setRaw] = useState(data.video_id);

  const invalid = raw.trim() !== '' && extractYoutubeId(raw) === null;

  function onUrlChange(value: string) {
    setRaw(value);
    const videoId = extractYoutubeId(value);
    onChange({ ...data, video_id: videoId ?? '' });
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor={inputId}>{t('curriculum:editor.youtube.url')}</Label>
        <Input
          id={inputId}
          value={raw}
          placeholder="https://www.youtube.com/watch?v=…"
          onChange={e => onUrlChange(e.target.value)}
          aria-invalid={invalid}
        />
        {invalid ? (
          <p className="text-destructive text-sm">{t('curriculum:editor.youtube.invalid')}</p>
        ) : null}
      </div>

      {data.video_id ? (
        <iframe
          src={youtubeEmbedUrl(data.video_id)}
          title={data.caption || t('curriculum:editor.youtube.previewTitle')}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="aspect-video w-full rounded-md border"
        />
      ) : null}

      <div className="space-y-1.5">
        <Label htmlFor={`${inputId}-caption`}>{t('curriculum:editor.youtube.caption')}</Label>
        <Input
          id={`${inputId}-caption`}
          value={data.caption ?? ''}
          maxLength={500}
          onChange={e => onChange({ ...data, caption: e.target.value })}
        />
      </div>
    </div>
  );
}
